import React, { useEffect, useMemo, useState } from "react";

// Formik validation
import { Pencil } from "lucide-react";

import { VehicleModelImport, GetVehicleModels } from "api/setup/index";

import BreadCrumb from "common/breadcrumb";
import LoadingComponent from "common/components/loader";
import TableContainer from "common/table-container";
import SearchNotFoundComponent from "common/components/search-not-found";
import FileUploadComponent from "admin-portal/shared/file-upload-component";
import { VehicleModelImportParam } from "models/setup/vehicle-model-import-param";
import { VehicleModel } from "models/setup/vehicle-model";

const VehicleModelImportPage: React.FC = () => {
  const title = "Vehicle Model";
  const pageTitle = "Setup";

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<VehicleModel[]>([]);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState<VehicleModel | undefined>();
  const [editMode, setEditMode] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const res = await GetVehicleModels();
    if (!(res?.status?.isError ?? false) && res?.data) {
      setData(res?.data ?? []);
      setTotal(res?.data?.length ?? 0);
    } else {
      setData([]);
      setTotal(0);
    }
    setLoading(false);
  };

  const handleEditClick = (id: number) => {
    const _edited = data.find((d) => d.id == id);
    setSelected(_edited);
    setEditMode(true);
    // formik.setValues(_edited); // Set formik values when prop changes
  };

  const columns = useMemo(
    () => [
      {
        header: "Make",
        accessorKey: "Make",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const { manufacturer, id, name } = cell.row.original;

          return <>{manufacturer.name}</>;
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "isActive",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const { isActive, id } = cell.row.original;

          return (
            <div className="flex items-center">
              <div className="relative inline-block w-10 align-middle transition duration-200 ease-in ltr:mr-2 rtl:ml-2">
                <input
                  type="checkbox"
                  id={`status-${id}`} // unique ID for each row
                  checked={isActive}
                  readOnly // making checkbox read-only to avoid user interaction
                  className="absolute block size-5 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 peer/published checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-green-500 dark:checked:border-green-500 arrow-none"
                />
                <label
                  htmlFor={`status-${id}`}
                  className="block h-5 overflow-hidden duration-300 ease-linear border rounded-full cursor-pointer transition border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked/published:bg-green-500 peer-checked/published:border-green-500"
                ></label>
              </div>
            </div>
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="flex justify-end gap-2">
            <div
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400 cursor-pointer"
              onClick={() => handleEditClick(cell.row.original.id)}
            >
              <Pencil className="size-4" />
            </div>
          </div>
        ),
      },
    ],
    []
  );

  const onFileUpload = async (selectedFiles: any[]) => {
    if (selectedFiles && selectedFiles.length == 1) {
      try {
        setError("");
        setSuccess("");
        setSaving(true);

        const base64File = await convertFileToBase64(selectedFiles[0]);

        var _object: VehicleModelImportParam = {
          type: base64File?.type ?? "",
          base64CSVFile: base64File?.base64 ?? "",
        };

        const res = await VehicleModelImport(_object);

        if (!(res?.status?.isError ?? false) && res?.data) {
          setSuccess("success");
          loadData();
        } else {
          setError(`error: ${res?.status?.errorCode ?? "500"}:${res?.status?.errorMessage ?? ""}`);
        }
        setSaving(false);
      } catch (error) {
        setError("Something went wrong...");
        setSaving(false);
      }
    }
  };

  const convertFileToBase64 = (file: File): Promise<any> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader && reader.result) {
          if (typeof reader.result === "string") {
            return resolve({ type: reader.result.split(",")[0], base64: reader.result.split(",")[1] });
          } else {
            setError("Error: FileReader result is not a string");
          }
        } else {
          return resolve(null);
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  return (
    <React.Fragment>
      <BreadCrumb title={title} pageTitle={pageTitle} />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5 min-w-[50%]">
        <div className="xl:col-span-6">
          <div className="card">
            <div className="card-body">
              <h6 className="mb-4 text-15"> Import Vehicle Models</h6>
              <FileUploadComponent
                supportedTypes={["text/csv"]}
                onFileUpload={onFileUpload}
                maxFileCount={1}
              ></FileUploadComponent>

              {saving && <LoadingComponent />}
              {success && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-green-500 border border-green-200 rounded-md bg-green-50 dark:bg-green-400/20 dark:border-green-500/50"
                  id="successAlert"
                >
                  <b>Success! </b>
                  {<>vehicle models has been imported successfully</>}
                </div>
              )}
              {error && (
                <div
                  className="px-4 py-3 mt-3 text-sm text-red-500 border border-red-200 rounded-md bg-red-50 dark:bg-red-400/20 dark:border-red-500/50"
                  id="successAlert"
                >
                  <>
                    Oops! Something went wrong while attempting to import vehicle models. Please try again later or
                    contact support for assistance.
                  </>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="xl:col-span-6">
          <div className="card">
            <div className="card-body">
              {/*   <h6 className="mb-4 text-15">Service Type</h6> */}
              <div className="mx-auto w-full">
                {loading ? (
                  <LoadingComponent></LoadingComponent>
                ) : (
                  <div className="overflow-x-auto mt-2">
                    {data && data.length > 0 ? (
                      <TableContainer
                        isPagination={true}
                        columns={columns || []}
                        data={data || []}
                        totalCount={total}
                        customPageSize={10}
                        divclassName="overflow-x-auto"
                        tableclassName="w-full whitespace-nowrap"
                        theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                        thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                        tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                        PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                      />
                    ) : (
                      <SearchNotFoundComponent title="Sorry! No Result Found">
                        <p className="mb-0 text-slate-500 dark:text-zink-200">
                          No vehicle models setup, please import vehicle models.
                        </p>
                      </SearchNotFoundComponent>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
export default VehicleModelImportPage;
